import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Building2, FolderKanban, Handshake, House, Users } from "lucide-react";
import { useTranslate } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  // AEGYRA : accueil (tableau de bord), projets, affaires, entreprises et contacts ; les paramètres
  // s'ouvrent par l'avatar de l'accueil.
  if (
    matchPath("/", location.pathname) ||
    matchPath("/settings", location.pathname) ||
    matchPath("/credit_balances/*", location.pathname) ||
    matchPath("/offer", location.pathname) ||
    matchPath("/organisation", location.pathname)
  ) {
    currentPath = "/";
  } else if (matchPath("/projects/*", location.pathname)) {
    currentPath = "/projects";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else {
    currentPath = false;
  }

  // Check if the app is running as a PWA (standalone mode)
  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  // Check if it's iOS on the web
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 h-14 border-t border-border bg-card"
      style={{
        // iOS bug: even though viewport is set correctly, the bottom safe area inset is not accounted for
        // So we manually add some padding to avoid the navigation being too close to the home bar
        paddingBottom: isPwa && isWebiOS ? 15 : undefined,
        // We use box-sizing: border-box, so the height contains the padding.
        // To actually increase the padding, we need to increase the height as well
        height:
          "calc(var(--spacing)) * 6" + (isPwa && isWebiOS ? " + 15px" : ""),
      }}
    >
      <div className="mx-auto flex max-w-md justify-center">
        <>
          <NavigationButton
            href="/"
            Icon={House}
            label={translate("crm.navigation.home")}
            isActive={currentPath === "/"}
          />
          <NavigationButton
            href="/projects"
            Icon={FolderKanban}
            label={translate("resources.projects.name", { smart_count: 2 })}
            isActive={currentPath === "/projects"}
          />
          <NavigationButton
            href="/deals"
            Icon={Handshake}
            label={translate("resources.deals.name", { smart_count: 2 })}
            isActive={currentPath === "/deals"}
          />
          <NavigationButton
            href="/companies"
            Icon={Building2}
            label={translate("resources.companies.name", { smart_count: 2 })}
            isActive={currentPath === "/companies"}
          />
          <NavigationButton
            href="/contacts"
            Icon={Users}
            label={translate("resources.contacts.name", {
              smart_count: 2,
            })}
            isActive={currentPath === "/contacts"}
          />
        </>
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      "flex-1 min-w-0 flex-col gap-1 h-auto py-2 px-0.5 has-[>svg]:px-0.5 rounded-md hover:bg-transparent",
      isActive ? "text-foreground" : "text-muted-foreground",
    )}
  >
    <Link to={href} aria-current={isActive ? "page" : undefined}>
      <Icon className={cn("size-6", isActive && "text-signal")} />
      <span className="max-w-full truncate text-[11px] font-semibold leading-4">
        {label}
      </span>
    </Link>
  </Button>
);
